// The member portal: the signed-in part of the public site. A short header with the
// member's name, a tab bar, then each tab's cards inside the site container. Tabs follow
// the Page access rules (portal.*); the API enforces the same rules whatever shows here.
import { useEffect, useState, type ReactNode } from "react";
import { Link, Navigate, NavLink, Route, Routes } from "react-router-dom";
import { api } from "./api";
import { Icon, type IconName } from "./icons";
import { MinecraftShell } from "./MinecraftV2";
import { History } from "./portal/History";
import { areaVisible, useArea, useSite, type SiteArea } from "./site/context";
import { MemberSupport } from "./tickets/Tickets";
import { ApiState, Avatar, Badge, CopyButton, EmptyState, Notice, Panel, Skeleton, errorMessage, formatDate, formatDateTime, formatHours, formatMoney, formatNumber, formatPlaytime, plural, useApi, useNotice, type Me } from "./ui";
import { Bars, useResource } from "./ui/kit";

type PortalTab = { area: string; to: string; label: string; icon: IconName };
const portalTabs: PortalTab[] = [
  { area: "portal.home", to: "/discord/portal", label: "Home", icon: "overview" },
  { area: "portal.activity", to: "/discord/portal/activity", label: "Activity", icon: "pulse" },
  { area: "portal.minecraft", to: "/discord/portal/minecraft", label: "Minecraft", icon: "cube" },
  { area: "portal.roles", to: "/discord/portal/roles", label: "Roles", icon: "roles" },
  { area: "portal.support", to: "/discord/portal/support", label: "Support", icon: "ticket" },
  { area: "portal.history", to: "/discord/portal/history", label: "History", icon: "history" },
  { area: "portal.profile", to: "/discord/portal/profile", label: "Profile", icon: "tag" },
];

// Tabs the viewer may open. Until access data loads every tab shows; the API enforces the same rules.
function useVisibleTabs() {
  const { me, areas } = useSite();
  return portalTabs.filter((tab) => areaVisible(areas?.find((area) => area.id === tab.area) || null, Boolean(me.user)));
}

export function Portal({ me }: { me: Me }) {
  const tabs = useVisibleTabs();
  const name = me.user?.displayName || "there";
  return <MinecraftShell><main className="portal" id="main">
    <header className="portal-head">
      <div className="container">
        <div className="portal-head-main">
          <Avatar src={me.user?.avatarUrl} name={name} size={64} square/>
          <div className="portal-head-copy">
            <h1>Welcome, {name}</h1>
            <p>Your stats, Minecraft accounts, roles, tickets, and record, all in one place.</p>
          </div>
          {me.permissions.includes("dashboard.view") && <Link className="button" to="/discord/admin/overview"><Icon name="overview" size={16}/>Staff dashboard</Link>}
        </div>
        {tabs.length > 1 && <nav className="portal-tabs" aria-label="Member portal">{tabs.map((tab) => <NavLink key={tab.to} end={tab.to === "/discord/portal"} to={tab.to}><Icon name={tab.icon} size={16}/><span>{tab.label}</span></NavLink>)}</nav>}
      </div>
    </header>
    <div className="container portal-body"><Routes>
      <Route index element={<TabGate area="portal.home" home><PortalHome/></TabGate>}/>
      <Route path="activity" element={<TabGate area="portal.activity"><PortalActivity/></TabGate>}/>
      <Route path="minecraft" element={<TabGate area="portal.minecraft"><PortalMinecraft/></TabGate>}/>
      <Route path="roles" element={<TabGate area="portal.roles"><PortalRoles/></TabGate>}/>
      <Route path="support/*" element={<TabGate area="portal.support"><MemberSupport/></TabGate>}/>
      <Route path="history" element={<TabGate area="portal.history"><History/></TabGate>}/>
      <Route path="profile" element={<TabGate area="portal.profile"><PortalProfile/></TabGate>}/>
      <Route path="*" element={<EmptyState icon="search" title="Nothing here" description="This part of the portal does not exist. The tabs above have everything there is." action={<Link className="button" to="/discord/portal">Back to portal home</Link>}/>}/>
    </Routes></div>
  </main></MinecraftShell>;
}

// A blocked Home sends the member to the first tab they may open; any other blocked tab explains why.
function TabGate({ area: id, home, children }: { area: string; home?: boolean; children: ReactNode }) {
  const area = useArea(id);
  const { me } = useSite();
  const tabs = useVisibleTabs();
  if (areaVisible(area, Boolean(me.user))) return <>{children}</>;
  const fallback = tabs.find((tab) => tab.area !== id);
  if (home && fallback) return <Navigate to={fallback.to} replace/>;
  return <RestrictedTab area={area as SiteArea} canGoHome={!home && tabs.some((tab) => tab.area === "portal.home")}/>;
}

function RestrictedTab({ area, canGoHome }: { area: SiteArea; canGoHome: boolean }) {
  const missing = area.available === false;
  const off = !area.enabled;
  const title = missing ? `${area.label} is not running` : off ? `${area.label} is turned off` : "Not available for your roles";
  const description = missing ? "This part of the portal needs a SkyBot add-on that is not running right now. Check back later."
    : off ? "The team has turned this part of the portal off for now. Check back later."
    : "Your Discord roles do not include this part of the portal. If you think that is wrong, ask a staff member.";
  return <EmptyState icon="lock" title={title} description={description} action={canGoHome ? <Link className="button" to="/discord/portal">Back to portal home</Link> : undefined}/>;
}

function toggleIn(current: Set<string>, id: string): Set<string> {
  const next = new Set(current);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}

// A row of numbers with a rule above each, the same rhythm as the steps on the home page.
type Stat = { label: string; value: string; hint?: string; icon: IconName };
function StatRow({ label, stats }: { label: string; stats: Stat[] }) {
  return <section aria-label={label}>
    <dl className="portal-stats">{stats.map((stat) => <div key={stat.label}>
      <dt><Icon name={stat.icon} size={16}/>{stat.label}</dt>
      <dd>{stat.value}</dd>
      {stat.hint && <dd className="portal-stat-hint">{stat.hint}</dd>}
    </div>)}</dl>
  </section>;
}

/* ---------- Home ---------- */

type MinecraftAccount = { minecraft_uuid: string; minecraft_name: string; linked_at: string; is_primary: number };
type PortalData = { pages: Array<{ id: number | string; label: string }>; minecraftAccounts?: MinecraftAccount[] };
type Rank = { rank: number | null; value: number | null; ranked: number } | null;
type PlayerSummary = { online: boolean; group?: string | null; balance: number | null; playtimeSeconds: number | null; platform?: string; island: { name: string | null; level: number | null } | null };
type Overview = {
  member: { displayName: string; username: string | null; avatarUrl: string | null; joinedAt: string | null; premiumSince: string | null; roles: Array<{ id: string; name: string; color: string | null }> };
  stats: { messages: number | null; voiceSeconds: number | null; activeDays: number | null; invites: number | null };
  ranks: Record<"messages" | "voicetime" | "invites", Rank>;
  server: { status: string; online: boolean; players: number; max: number; version: string | null };
  connect: { java: string | null; bedrock: string | null };
  minecraft: { primary: { minecraft_name: string } | null; accounts: number; profile: PlayerSummary | null };
  tags: Array<{ id: number | string; label: string; icon: string | null }>;
  optedOut: boolean;
};

const statValue = (value: number | null, format: (value: number) => string) => value === null || value === undefined ? "—" : format(value);

function PortalHome() {
  const state = useApi<Overview>("/api/v1/portal/overview");
  const portal = useApi<PortalData>("/api/v1/portal");
  if (!state.data) return state.loading ? <Skeleton rows={4} label="Loading your overview"/> : <ApiState state={state}/>;
  const data = state.data;
  const untracked = "Not tracked yet";
  return <>
    <StatRow label="Your last 30 days" stats={[
      { icon: "message", label: "Messages, 30 days", value: statValue(data.stats.messages, formatNumber), hint: data.stats.messages === null ? untracked : undefined },
      { icon: "mic", label: "Voice, 30 days", value: statValue(data.stats.voiceSeconds, formatHours), hint: data.stats.voiceSeconds === null ? untracked : undefined },
      { icon: "calendar", label: "Active days", value: statValue(data.stats.activeDays, (value) => `${formatNumber(value)} of 30`), hint: data.stats.activeDays === null ? untracked : undefined },
      { icon: "invite", label: "People invited", value: statValue(data.stats.invites, formatNumber), hint: data.stats.invites === null ? untracked : "Invites that stayed, 30 days" },
    ]}/>
    <div className="portal-split">
      <ProfileSummary data={data}/>
      <ServerSummary data={data}/>
    </div>
    <div className="portal-trio">
      <Standing data={data}/>
      <MinecraftSummary data={data} portal={portal.data} loading={portal.loading}/>
      <Shortcuts pages={portal.data?.pages || []}/>
    </div>
  </>;
}

function ProfileSummary({ data }: { data: Overview }) {
  const { member, tags } = data;
  const profileTab = useArea("portal.profile");
  const { me } = useSite();
  return <Panel title="Your profile" action={areaVisible(profileTab, Boolean(me.user)) ? <Link className="button compact" to="/discord/portal/profile">Edit</Link> : undefined}>
    <dl className="portal-facts">
      {member.username && <div><dt>Username</dt><dd>@{member.username}</dd></div>}
      <div><dt>Member since</dt><dd>{member.joinedAt ? formatDate(member.joinedAt) : "—"}</dd></div>
      {member.premiumSince && <div><dt>Boosting since</dt><dd>{formatDate(member.premiumSince)}</dd></div>}
      <div><dt>Leaderboards</dt><dd>{data.optedOut ? "Hidden" : "Visible"}</dd></div>
    </dl>
    {member.roles.length > 0 || tags.length > 0 ? <div className="portal-chip-groups">
      {member.roles.length > 0 && <div><h3 className="portal-label">Roles</h3><ul className="chip-list portal-chips" aria-label="Your Discord roles">{member.roles.map((role) => <li key={role.id}><span className="role-swatch" style={role.color && role.color !== "#000000" ? { background: role.color } : undefined} aria-hidden="true"/>{role.name}</li>)}</ul></div>}
      {tags.length > 0 && <div><h3 className="portal-label">Profile tags</h3><ul className="chip-list portal-chips" aria-label="Your profile tags">{tags.map((tag) => <li key={tag.id}>{tag.icon && <span aria-hidden="true">{tag.icon}</span>}{tag.label}</li>)}</ul></div>}
    </div> : <p className="portal-muted">No roles or profile tags to show yet.</p>}
  </Panel>;
}

function ServerSummary({ data }: { data: Overview }) {
  const { server, connect } = data;
  return <Panel title="SkyBoxy MC">
    <p className="portal-server-status">
      <span className={`live-pulse ${server.online ? "" : "paused"}`} aria-hidden="true"/>
      {server.online ? <span><strong>{plural(server.players, "player")}</strong> online{server.max ? ` of ${formatNumber(server.max)}` : ""}{server.version ? ` · ${server.version}` : ""}</span> : <span>Status unavailable. The server may be restarting.</span>}
    </p>
    {connect.java || connect.bedrock ? <ul className="portal-addresses">
      {connect.java && <li><span><small>Java</small><strong>{connect.java}</strong></span><CopyButton value={connect.java} label="Copy" className="button compact"/></li>}
      {connect.bedrock && <li><span><small>Bedrock</small><strong>{connect.bedrock}</strong></span><CopyButton value={connect.bedrock} label="Copy" className="button compact"/></li>}
    </ul> : <p className="portal-muted">Server addresses have not been set up yet.</p>}
  </Panel>;
}

const standingRows: Array<{ key: "messages" | "voicetime" | "invites"; label: string; icon: IconName; format: (value: number) => string }> = [
  { key: "messages", label: "Messages", icon: "message", format: formatNumber },
  { key: "voicetime", label: "Voice time", icon: "mic", format: formatHours },
  { key: "invites", label: "Invites", icon: "invite", format: formatNumber },
];

function Standing({ data }: { data: Overview }) {
  return <Panel title="Your standing" description="Where you sit on this month's Discord leaderboards.">
    <ul className="portal-rows">{standingRows.map((row) => {
      const rank = data.ranks[row.key];
      const text = !rank ? "Not tracked" : data.optedOut ? "Hidden" : rank.rank ? `#${formatNumber(rank.rank)}` : "Not ranked yet";
      return <li key={row.key}>
        <span className="tile-icon" aria-hidden="true"><Icon name={row.icon} size={16}/></span>
        <span className="portal-row-copy"><strong>{row.label}</strong><small>{!rank ? "This leaderboard is not running" : rank.value !== null && !data.optedOut ? `${row.format(rank.value)} this month` : `${plural(rank.ranked, "member")} ranked`}</small></span>
        <Badge tone={rank?.rank && !data.optedOut ? "accent" : "neutral"}>{text}</Badge>
      </li>;
    })}</ul>
    {data.optedOut && <p className="portal-muted">You are hidden from the leaderboards. You can change that on your profile.</p>}
    <Link className="section-link" to="/discord/leaderboard">See the leaderboards<Icon name="arrowRight" size={16}/></Link>
  </Panel>;
}

function MinecraftSummary({ data, portal, loading }: { data: Overview; portal?: PortalData; loading: boolean }) {
  const mcTab = useArea("portal.minecraft");
  const { me } = useSite();
  const canManage = areaVisible(mcTab, Boolean(me.user));
  const accounts = portal?.minecraftAccounts || [];
  const profile = data.minecraft.profile;
  return <Panel title="Minecraft" action={canManage && (accounts.length || data.minecraft.primary) ? <Link className="button compact" to="/discord/portal/minecraft">Manage</Link> : undefined}>
    {!portal && loading ? <Skeleton rows={1} label="Loading linked accounts"/>
      : accounts.length || data.minecraft.primary ? <>
        {accounts.length ? <MinecraftAccountList accounts={accounts} online={profile?.online}/> : <p><strong>{data.minecraft.primary?.minecraft_name}</strong> <span className="portal-muted">is linked</span></p>}
        {profile ? <dl className="portal-facts">
          {profile.island?.level !== null && profile.island?.level !== undefined && <div><dt>Island level</dt><dd>{formatNumber(profile.island.level)}</dd></div>}
          {profile.balance !== null && <div><dt>Balance</dt><dd>{formatMoney(profile.balance)}</dd></div>}
          {profile.playtimeSeconds !== null && <div><dt>Played</dt><dd>{formatPlaytime(profile.playtimeSeconds)}</dd></div>}
        </dl> : <p className="portal-muted">Island and balance stats show up here when the server is reachable.</p>}
      </>
      : <div className="portal-empty-inline">
        <p>No Minecraft account linked yet. Link one to see your island level, balance, and play time here.</p>
        {canManage && <Link className="button primary compact" to="/discord/portal/minecraft"><Icon name="link" size={16}/>Link Minecraft</Link>}
      </div>}
  </Panel>;
}

function Shortcuts({ pages }: { pages: PortalData["pages"] }) {
  const { me } = useSite();
  const signedIn = Boolean(me.user);
  const roles = useArea("portal.roles"), support = useArea("portal.support"), profile = useArea("portal.profile"), minecraft = useArea("portal.minecraft"), history = useArea("portal.history");
  const items: Array<[boolean, string, IconName, string, string]> = [
    [areaVisible(support, signedIn), "/discord/portal/support/new", "ticket", "Get help", "Open a ticket with the team"],
    [areaVisible(roles, signedIn), "/discord/portal/roles", "roles", "Choose your roles", "Pick your own Discord roles"],
    [areaVisible(profile, signedIn), "/discord/portal/profile", "tag", "Edit your profile", "Tags and leaderboard privacy"],
    [areaVisible(minecraft, signedIn), "/discord/portal/minecraft", "link", "Link Minecraft", "Connect a Java or Bedrock account"],
    [areaVisible(history, signedIn), "/discord/portal/history", "history", "Your record", "Warnings, punishments, and appeals"],
    [true, "/minecraft/islandtop", "trophy", "Server leaderboards", "Island top, balance, and play time"],
  ];
  return <Panel title="Shortcuts">
    <ul className="portal-links">{items.filter(([visible]) => visible).map(([, to, icon, title, detail]) => <li key={to}><Link to={to}>
      <span className="tile-icon" aria-hidden="true"><Icon name={icon} size={16}/></span>
      <span className="portal-row-copy"><strong>{title}</strong><small>{detail}</small></span>
      <Icon name="chevronRight" size={16}/>
    </Link></li>)}</ul>
    {pages.length > 0 && <div><h3 className="portal-label">Pages for your roles</h3><ul className="chip-list">{pages.map((page) => <li key={page.id}>{page.label}</li>)}</ul></div>}
  </Panel>;
}

function MinecraftAccountList({ accounts, online }: { accounts: MinecraftAccount[]; online?: boolean }) {
  if (!accounts.length) return <p className="portal-muted">No accounts linked yet.</p>;
  return <ul className="portal-accounts" aria-label="Linked Minecraft accounts">{accounts.map((account) => <li key={account.minecraft_uuid}>
    <Avatar src={`https://mc-heads.net/avatar/${encodeURIComponent(account.minecraft_name)}/40`} name={account.minecraft_name} size={40} square/>
    <span className="portal-row-copy"><strong>{account.minecraft_name}</strong><small>{account.is_primary && online !== undefined ? `${online ? "Online now" : "Offline"} · ` : ""}Linked {formatDate(account.linked_at)}</small></span>
    <Badge tone={account.is_primary ? "accent" : "neutral"}>{account.is_primary ? "Main" : "Alt"}</Badge>
  </li>)}</ul>;
}

/* ---------- Activity ---------- */

type ActivityData = { available: boolean; summary: null | { messages: number; voiceSeconds: number; activeDays: number; replies: number | null; reactionsGiven: number | null }; series: Array<{ day: string; messages: number; voiceSeconds: number }> };

function PortalActivity() {
  const state = useApi<ActivityData>("/api/v1/portal/activity");
  if (!state.data) return state.loading ? <Skeleton rows={3} label="Loading your activity"/> : <ApiState state={state}/>;
  const { available, summary, series } = state.data;
  if (!available || !summary) return <EmptyState icon="pulse" title="Activity is not being tracked" description="Message and voice stats are not being collected right now, so there is nothing to show yet."/>;
  const stats: Stat[] = [
    { icon: "message", label: "Messages", value: formatNumber(summary.messages), hint: "Last 30 days" },
    { icon: "mic", label: "Time in voice", value: formatHours(summary.voiceSeconds), hint: "Last 30 days" },
    { icon: "calendar", label: "Active days", value: `${formatNumber(summary.activeDays)} of 30`, hint: "Days with a message or voice time" },
  ];
  if (summary.reactionsGiven !== null) stats.push({ icon: "spark", label: "Reactions given", value: formatNumber(summary.reactionsGiven), hint: "Last 30 days" });
  return <>
    <StatRow label="Your last 30 days" stats={stats}/>
    <div className="portal-pair">
      <Panel title="Messages per day" description={`${formatNumber(summary.messages)} messages over the last 30 days, one bar per day.`}><Bars series={series} valueKey="messages" label="Messages per day" format={formatNumber}/></Panel>
      <Panel title="Voice time per day" description={`${formatHours(summary.voiceSeconds)} in voice channels over the last 30 days.`}><Bars series={series} valueKey="voiceSeconds" label="Voice time per day" format={formatHours}/></Panel>
    </div>
  </>;
}

/* ---------- Roles ---------- */

function PortalRoles() {
  const state = useApi<any>("/api/v1/portal/roles");
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [initial, setInitial] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const notice = useNotice();
  useEffect(() => { if (state.data) { const selected = new Set<string>(state.data.panels.flatMap((panel: any) => panel.mappings.filter((mapping: any) => mapping.selected).map((mapping: any) => mapping.roleId))); setChosen(selected); setInitial(new Set(selected)); } }, [state.data]);
  if (!state.data) return state.loading ? <Skeleton rows={3} label="Loading roles"/> : <ApiState state={state}/>;
  const panels: any[] = state.data.panels.filter((panel: any) => panel.mappings.length);
  const changed = [...chosen].filter((id) => !initial.has(id)).length + [...initial].filter((id) => !chosen.has(id)).length;
  const toggle = (id: string) => setChosen((current) => toggleIn(current, id));
  const save = () => { setSaving(true); api<any>("/api/v1/portal/roles", { method: "PUT", body: JSON.stringify({ roleIds: [...chosen], idempotencyKey: crypto.randomUUID() }) }).then((result) => { if (result.state === "partial") notice.error("Some role changes could not be applied."); else { notice.success("Role changes saved."); setInitial(new Set(chosen)); } }).catch((error) => notice.error(errorMessage(error, "Role update failed."))).finally(() => setSaving(false)); };
  return <Panel title="Self-service roles" description="Tick the roles you want, then save. We check each one against your Discord roles when you save.">
    <Notice notice={notice.notice} onDismiss={notice.clear}/>
    {panels.length ? panels.map((panel) => <fieldset className="choice-group" key={panel.id ?? panel.title}>
      <legend>{panel.title || panel.name}</legend>
      <div className="choice-grid">{panel.mappings.map((mapping: any) => <label key={mapping.roleId} className="choice-card"><input type="checkbox" checked={chosen.has(mapping.roleId)} onChange={() => toggle(mapping.roleId)}/><span className="choice-emoji" aria-hidden="true">{mapping.emoji}</span><span className="choice-copy"><strong>{mapping.description || mapping.roleId}</strong></span><span className="choice-check" aria-hidden="true"><Icon name="check" size={14}/></span></label>)}</div>
    </fieldset>) : <EmptyState compact icon="roles" title="No roles to pick yet" description="The team has not set up any roles you can choose yourself."/>}
    {panels.length > 0 && <div className="portal-savebar"><span className="portal-muted" aria-live="polite">{changed ? plural(changed, "unsaved change") : "No changes"}</span><div className="form-actions"><button className="button" type="button" disabled={!changed || saving} onClick={() => setChosen(new Set(initial))}>Discard</button><button className="button primary" type="button" disabled={!changed || saving} onClick={save}>{saving ? "Saving…" : "Save roles"}</button></div></div>}
  </Panel>;
}

/* ---------- Profile ---------- */

function PortalProfile() {
  const state = useApi<any>("/api/v1/portal/tags");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const notice = useNotice();
  useEffect(() => { if (state.data) setSelected(new Set(state.data.items.filter((item: any) => item.selected).map((item: any) => item.id))); }, [state.data]);
  const toggle = (id: string) => setSelected((current) => toggleIn(current, id));
  const save = () => { setSaving(true); api("/api/v1/portal/tags", { method: "PUT", body: JSON.stringify({ tagIds: [...selected] }) }).then(() => notice.success("Profile tags saved.")).catch((error) => notice.error(errorMessage(error, "Could not save tags."))).finally(() => setSaving(false)); };
  return <div className="portal-split">
    <Panel title="Profile tags" description="Pick the tags that show on your profile. Some tags depend on your Discord roles.">
      <Notice notice={notice.notice} onDismiss={notice.clear}/>
      {!state.data ? state.loading ? <Skeleton rows={2} label="Loading profile tags"/> : <ApiState state={state}/>
        : state.data.items.length ? <div className="choice-grid">{state.data.items.map((item: any) => <label key={item.id} className="choice-card"><input type="checkbox" checked={selected.has(item.id)} onChange={() => toggle(item.id)}/><span className="choice-emoji" aria-hidden="true" style={item.color ? { color: item.color } : undefined}>{item.icon}</span><span className="choice-copy"><strong>{item.label}</strong>{item.description && <small>{item.description}</small>}</span><span className="choice-check" aria-hidden="true"><Icon name="check" size={14}/></span></label>)}</div>
        : <EmptyState compact icon="tag" title="No profile tags yet" description="The team has not added any profile tags."/>}
      {state.data?.items.length > 0 && <div className="form-actions"><button className="button primary" type="button" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save tags"}</button></div>}
    </Panel>
    <LeaderboardPrivacy/>
  </div>;
}

function LeaderboardPrivacy() {
  const state = useApi<{ leaderboardOptOut: boolean }>("/api/v1/portal/privacy");
  const [hidden, setHidden] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const notice = useNotice();
  useEffect(() => { if (state.data) setHidden(state.data.leaderboardOptOut); }, [state.data]);
  const change = (show: boolean) => {
    const previous = hidden;
    setHidden(!show); setSaving(true); notice.clear();
    api<{ leaderboardOptOut: boolean }>("/api/v1/portal/privacy", { method: "PUT", body: JSON.stringify({ leaderboardOptOut: !show }) })
      .then((result) => { setHidden(result.leaderboardOptOut); notice.success(result.leaderboardOptOut ? "You are now hidden from the Discord leaderboards." : "You appear on the Discord leaderboards again."); })
      .catch((error) => { setHidden(previous); notice.error(errorMessage(error, "Could not save your privacy setting.")); })
      .finally(() => setSaving(false));
  };
  return <Panel title="Leaderboard privacy" description="The public Discord leaderboards show names and avatars. Your stats keep counting either way.">
    <Notice notice={notice.notice} onDismiss={notice.clear}/>
    {hidden === null ? state.loading ? <Skeleton rows={1} label="Loading privacy setting"/> : <ApiState state={state}/>
      : <label className={`switch ${saving ? "is-disabled" : ""}`}>
        <span className="switch-control"><input type="checkbox" role="switch" checked={!hidden} disabled={saving} onChange={(event) => change(event.target.checked)}/><span className="switch-track" aria-hidden="true"><span className="switch-thumb"/></span></span>
        <span className="switch-copy"><span className="switch-label">Show me on leaderboards</span><small>{hidden ? "Hidden. Your name does not appear on public rankings." : "Visible. Your name and avatar can appear on public rankings."}</small></span>
      </label>}
  </Panel>;
}

/* ---------- Minecraft ---------- */

type PlayerProfile = { link: { minecraft_name: string }; profile: { status: string; data: null | (PlayerSummary & { platform?: string }) } | null };

function PortalMinecraft() {
  const state = useApi<PortalData>("/api/v1/portal");
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Live stats exist only for a main account; asking without one returns 404.
  const linked = Boolean(state.data?.minecraftAccounts?.some((account) => account.is_primary));
  const player = useResource<PlayerProfile>(linked ? "/api/v1/portal/minecraft/player" : null);
  if (!state.data) return state.loading ? <Skeleton rows={3} label="Loading your accounts"/> : <ApiState state={state}/>;
  const accounts: MinecraftAccount[] = state.data.minecraftAccounts || [];
  const live = player.data?.profile?.data || null;
  const linkLabel = accounts.length ? "Link an alt account" : "Link your account";
  const create = async () => { setBusy(true); setError(""); try { setCode(await api<any>("/api/v1/portal/minecraft/link-code", { method: "POST" })); } catch (reason) { setError(errorMessage(reason, "Could not create a link code.")); } finally { setBusy(false); } };
  return <div className="portal-pair">
    <Panel title="Your Minecraft accounts" description="The first account you link is your main one. Any others count as alts, and server features always use your main account.">
      <MinecraftAccountList accounts={accounts} online={live?.online}/>
      {linked && <div className="portal-live">
        <h3 className="portal-label">Live from the server</h3>
        {player.loading && !player.data ? <Skeleton rows={1} label="Loading live stats"/>
          : live ? <dl className="portal-facts">
            <div><dt>Status</dt><dd className="portal-online"><span className={`live-pulse ${live.online ? "" : "paused"}`} aria-hidden="true"/>{live.online ? "Online" : "Offline"}</dd></div>
            {live.group && <div><dt>Rank</dt><dd>{live.group}</dd></div>}
            {live.balance !== null && live.balance !== undefined && <div><dt>Balance</dt><dd>{formatMoney(live.balance)}</dd></div>}
            {live.playtimeSeconds !== null && live.playtimeSeconds !== undefined && <div><dt>Played</dt><dd>{formatPlaytime(live.playtimeSeconds)}</dd></div>}
            {live.island?.level !== null && live.island?.level !== undefined && <div><dt>Island level</dt><dd>{formatNumber(live.island.level)}</dd></div>}
            {live.platform && live.platform !== "unknown" && <div><dt>Edition</dt><dd>{live.platform === "bedrock" ? "Bedrock" : "Java"}</dd></div>}
          </dl>
          : <p className="portal-muted">{player.error ? errorMessage(player.error, "Live stats are unavailable right now.") : "Live stats are unavailable while the server is unreachable."}</p>}
      </div>}
    </Panel>
    <Panel title={linkLabel} description="Get a private code here, then type a command in Minecraft on the account you want to link.">
      <ol className="portal-steps"><li>Get a one-time code below.</li><li>Join SkyBoxy MC on that account.</li><li>Type <code>/link &lt;code&gt;</code> in chat.</li></ol>
      {code ? <div className="link-code" role="status"><span className="portal-muted">Your private code</span><strong className="code-display">{code.code.split("").map((digit, index) => <span key={index}>{digit}</span>)}</strong><span className="portal-muted">Expires {formatDateTime(code.expiresAt)}. Works once.</span><CopyButton value={`/link ${code.code}`} label="Copy /link command"/></div>
        : <div><button type="button" className="button primary" disabled={busy} onClick={create}><Icon name="link" size={16}/>{busy ? "Getting a code…" : "Get a link code"}</button></div>}
      {code && <div><button type="button" className="button ghost compact" disabled={busy} onClick={create}><Icon name="refresh" size={16}/>Get a new code</button></div>}
      {error && <p className="notice tone-error" role="alert"><Icon name="alert" size={18}/><span>{error}</span></p>}
    </Panel>
  </div>;
}
